import type { CSSProperties } from 'react';

export type VideoRotationDegrees = 0 | 90 | 180 | 270;

export function normalizeVideoRotationDegrees(degrees: number | undefined): VideoRotationDegrees {
  const normalized = ((degrees ?? 0) % 360 + 360) % 360;
  switch (normalized) {
    case 90:
      return 90;
    case 180:
      return 180;
    case 270:
      return 270;
    default:
      return 0;
  }
}

export function nextVideoRotationDegrees(
  current: number | undefined,
  delta: 90 | -90,
): VideoRotationDegrees {
  return normalizeVideoRotationDegrees((current ?? 0) + delta);
}

export function walkthroughVideoObjectClass(degrees: number | undefined): string {
  return normalizeVideoRotationDegrees(degrees) === 0 ? 'object-cover' : 'object-contain';
}

export function walkthroughVideoRotationStyle(degrees: number | undefined): CSSProperties {
  const rotation = normalizeVideoRotationDegrees(degrees);
  if (rotation === 0) {
    return {};
  }
  return {
    transform: `rotate(${rotation}deg)`,
    transformOrigin: 'center center',
  };
}

/**
 * Modern browsers apply QuickTime display matrices themselves. Applying the
 * same rotation again in CSS turns a correctly decoded phone video upside down.
 * Stored rotation remains useful for formats whose metadata is not honoured.
 */
export function walkthroughBrowserRotationDegrees(
  videoUrl: string | undefined,
  storedDegrees: number | undefined,
): VideoRotationDegrees {
  if (videoUrl && /\.mov(?:\?|$)/i.test(videoUrl)) {
    return 0;
  }
  return normalizeVideoRotationDegrees(storedDegrees);
}
